import { CSSProperties, useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import { useScreenSize } from "shared/lib/useScreenSize"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { RowRendererProps } from "shared/ui/table/types"

import { formatProjectPosition } from "entities/projects/lib/formatProjectPosition"
import { useProductPositionTableRowPayload } from "widgets/product-position-table/contexts/ProductPositionTableRowPayloadContext"
import { getProductPositionTableProductPositionAddonColumnSizes } from "widgets/product-position-table/getProductPositionTableColumnSizes"

interface AddonRowViewModelProps
  extends RowRendererProps<ProductPositionAddon> {
  title?: string | null
}

export const useProductPositionTableProductPositionAddonRowViewModel = (
  props: AddonRowViewModelProps
) => {
  const { isTablet } = useScreenSize()
  const { item, parentIndices, level, rowElementProps, onCollapse } = props
  const {
    pendingProjectChildren,
    hideQuantity,
    showClarificationRequestColumn,
    showSelectionCheckbox
  } = useProductPositionTableRowPayload()

  const style: CSSProperties = useMemo(
    () => ({
      ...rowElementProps?.style,
      ...getProductPositionTableProductPositionAddonColumnSizes({
        level,
        hideVariantKey: isTablet,
        hideQuantity,
        showClarificationRequestColumn,
        showSelectionCheckbox
      })
    }),
    [
      hideQuantity,
      isTablet,
      level,
      rowElementProps?.style,
      showClarificationRequestColumn,
      showSelectionCheckbox
    ]
  )

  const isPending = !!pendingProjectChildren?.includes(item.id)

  const position = useMemo(
    () => formatProjectPosition(parentIndices),
    [parentIndices]
  )

  const handleCollapse = useEvent((e: React.MouseEvent) => {
    e.stopPropagation()
    onCollapse()
  })

  return {
    ...props,
    style,
    position,
    isPending,
    showClarificationRequestColumn,
    showSelectionCheckbox,
    onCollapse: handleCollapse
  }
}
